import { FormHelperText, Typography } from "@mui/material"
import { FieldArray } from "formik"
import React, { useEffect, useRef } from "react"
import OptionTemplate from "./OptionTemplate"

const DraggableImageOptions = ({
  formik,
  getOptionValues = () => {},
  getQuestionType = () => {},
  getOptionPath = () => {},
  questionInitOption
}) => {
  const dragItem = useRef()
  const dragOverItem = useRef()
  const draggable = useRef()
  const uid = useRef(1)
  const optionErrors = getOptionValues(formik?.errors) || ""
  const optionTouched = getOptionValues(formik?.touched) || ""
  const options = getOptionValues(formik.values) || []
  const questionType = getQuestionType(formik.values)

  const onAddChoiceClick = (options) => {
    options.push({
      uid: uid.current++,
      name: "",
      image: "",
      isCorrect: false
    })
  }

  const dragStart = (e, position) => {
    dragItem.current = position
    draggable.current = e.currentTarget.closest(".optionItem")
    draggable.current.style.cursor = "no-drop"
  }

  const dragEnter = (e) => {
    draggable.current.style.cursor = "copy"
    const targetChild = e.currentTarget.closest(".optionItem")
    if (!targetChild || !draggable.current) return
    const parentNode = targetChild.parentNode
    const siblings = Array.from(parentNode.children)
    const targetIndex = siblings.indexOf(targetChild)
    const currentIndex = siblings.indexOf(draggable.current)

    // Insert before when moving upwards, after when moving downwards
    if (targetIndex < currentIndex) {
      parentNode.insertBefore(draggable.current, targetChild)
    } else {
      parentNode.insertBefore(draggable.current, targetChild.nextSibling)
    }

    // Update the current drop index to the draggable's new position
    dragOverItem.current = Array.from(parentNode.children).indexOf(draggable.current)
  }

  const drop = (options) => {
    if (dragItem.current === dragOverItem.current) {
      return
    }
    const dragItemContent = getOptionValues(formik.values)?.[dragItem.current]
    options.remove(dragItem.current)
    options.insert(dragOverItem.current, dragItemContent)
    dragItem.current = null
    dragOverItem.current = null
  }

  const canAddMoreChoices = (values) => {
    const maxChoices = getQuestionType(values) === "2-images" ? 2 : 4
    const currentChoices = getOptionValues(values)?.length || 0
    return currentChoices < maxChoices
  }

  useEffect(() => {
    if (questionType === "2-images" && options.length > 2) {
      const trimmedOptions = options.slice(0, 2)
      formik.setFieldValue(getOptionPath(), trimmedOptions)
    } else if (questionType === "4-images" || questionType === "text") {
      if (questionInitOption?.length === 4)
        formik.setFieldValue(getOptionPath(), questionInitOption)
    }
  }, [questionType])

  return (
    <ul className="m-0 p-0">
      <FieldArray
        name={getOptionPath()}
        render={(arrayHelper) => (
          <>
            <li
              key={"draghelper"}
              className="py-3 optionItem"
              style={{ listStyleType: "none", marginTop: 3 }}
              onDragEnter={dragEnter}
              onDragEnd={() => drop(arrayHelper)}
            />
            {getOptionValues(formik.values)?.map((option, index) => (
              <OptionTemplate
                key={`optionItem-${option?.id || option?.uid}-${index}`}
                arrayHelper={arrayHelper}
                index={index}
                formik={formik}
                getOptionValues={getOptionValues}
                getOptionPath={getOptionPath}
                questionType={questionType}
                {...{ dragStart, dragEnter, drop }}
              />
            ))}
            {!Array.isArray(optionErrors) && Boolean(optionTouched) && Boolean(optionErrors) ? (
              <FormHelperText error={true}>{optionErrors}</FormHelperText>
            ) : null}
            {canAddMoreChoices(formik.values) ? (
              <Typography
                variant="caption2"
                role="button"
                onClick={() => onAddChoiceClick(arrayHelper)}
                sx={(theme) => ({
                  color: theme.palette.secondary.main,
                  textDecoration: "underline",
                  textUnderlineOffset: "2px"
                })}>
                Add Choice
              </Typography>
            ) : null}
          </>
        )}
      />
    </ul>
  )
}

export default DraggableImageOptions
